@if (vm$ | async; as vm) {
  <div
    class="carousel-container"
    [class.with-thumbnails]="navigationMode === 'thumbnails'">

    @if (vm.isLoading) {
      <div class="carousel-loading">
        <mat-progress-spinner
          mode="indeterminate"
          diameter="40"
          strokeWidth="4">
        </mat-progress-spinner>
      </div>
    }

    @if (!vm.isLoading) {
      @if (vm.currentImage; as image) {
        <div class="carousel">

          <div
            class="image-wrapper"
            [class.panzoom-enabled]="enablePanzoom">

            @if (!image.error && image.url) {
              <div class="panzoom-viewport">

                <div
                  class="scene"
                  [class.scene-static]="!enablePanzoom"
                  appPanzoom
                  #pz="appPanzoom"
                  [disabled]="!enablePanzoom"
                  [style.width.px]="enablePanzoom ? sceneWidth : null"
                  [style.height.px]="enablePanzoom ? sceneHeight : null">

                  <div
                    class="media-layer"
                    [style.width.px]="imgNatural?.w"
                    [style.height.px]="imgNatural?.h"
                    [style.transform]="mediaTransform">

                    <img
                      #img
                      [src]="image.url"
                      [alt]="image.attachment.filename"
                      class="carousel-image"
                      (load)="onImageLoad(img, image)"
                      (error)="onImageError(image)" />

                    @if (
                      showOverlay &&
                      showSVG &&
                      imgNatural &&
                      overlayPolygonPoints
                      ) {
                      <svg
                        class="overlay"
                        [attr.viewBox]="'0 0 ' + imgNatural.w + ' ' + imgNatural.h"
                        preserveAspectRatio="none">

                        <polygon
                          [attr.points]="overlayPolygonPoints"
                          [style.stroke]="
                            image.attachment.polygonHexColor || '#ff3b30'
                          "
                          class="svg-polygon">
                        </polygon>

                      </svg>
                    }

                  </div>
                </div>
              </div>
            } @else {
              <div class="image-error">
                <mat-icon>broken_image</mat-icon>
              </div>
            }

            <!-- =========================
                 TOP ACTIONS
            ========================== -->

            <div class="top-actions">

              <!-- FAVORITE -->
              @if (
                allowFavorite &&
                vm.currentImage &&
                vm.images.length > 1
                ) {
                <button
                  type="button"
                  class="overlay-button favorite-button"
                  [class.favorite-active]="isFavorite(vm.currentImage)"
                  [matTooltip]="
                    (
                      isFavorite(vm.currentImage)
                        ? 'favorite'
                        : 'set_as_favorite'
                    ) | transloco
                  "
                  (click)="toggleFavorite(vm.currentImage)">

                  <mat-icon>
                    {{
                      isFavorite(vm.currentImage)
                        ? 'star'
                        : 'star_border'
                    }}
                  </mat-icon>

                </button>
              }

              <!-- SVG OVERLAY -->
              @if (overlayPolygonPoints) {
                <button
                  type="button"
                  class="overlay-button svg-toggle-button"
                  [matTooltip]="
                    (
                      showSVG
                        ? 'hide_overlay'
                        : 'show_overlay'
                    ) | transloco
                  "
                  (click)="toggleSVG()">

                  <mat-icon>
                    {{
                      showSVG
                        ? 'visibility'
                        : 'visibility_off'
                    }}
                  </mat-icon>

                </button>
              }

              <!-- ROTATE -->
              @if (allowRotate) {
                <button
                  type="button"
                  class="overlay-button rotate-button"
                  [matTooltip]="'rotate_right' | transloco"
                  (click)="rotateRight()">

                  <mat-icon>
                    rotate_right
                  </mat-icon>

                </button>
              }

              <!-- DOWNLOAD -->
              @if (allowDownload && image.attachment) {
                <button
                  type="button"
                  class="overlay-button download-button"
                  [matTooltip]="
                    (
                      showSVG
                        ? 'download_image_with_overlay'
                        : 'download_original_image'
                    ) | transloco
                  "
                  (click)="downloadImage(image)"
                  [disabled]="!!downloading[image.attachment.path]">

                  <span class="overlay-button-content">

                    @if (!downloading[image.attachment.path]) {
                      <mat-icon>
                        download
                      </mat-icon>
                    }

                    @if (downloading[image.attachment.path]) {
                      <mat-spinner
                        diameter="24">
                      </mat-spinner>
                    }

                  </span>

                </button>
              }

            </div>

            <!-- =========================
                 PREVIOUS / NEXT
            ========================== -->

            @if (vm.images.length > 1) {
              <button
                type="button"
                class="overlay-button nav-button nav-left"
                (click)="
                  goTo(
                    vm.currentIndex === 0
                      ? vm.images.length - 1
                      : vm.currentIndex - 1
                  )
                ">

                <mat-icon>
                  chevron_left
                </mat-icon>

              </button>
            }

            @if (vm.images.length > 1) {
              <button
                type="button"
                class="overlay-button nav-button nav-right"
                (click)="
                  goTo(
                    vm.currentIndex === vm.images.length - 1
                      ? 0
                      : vm.currentIndex + 1
                  )
                ">

                <mat-icon>
                  chevron_right
                </mat-icon>

              </button>
            }

            <!-- =========================
                 DOT NAVIGATION
            ========================== -->

            @if (
              navigationMode === 'dots' &&
              vm.images.length > 1
              ) {
              <div class="overlay-dots">

                @for (
                  item of vm.images;
                  track trackByFilename(i, item);
                  let i = $index
                  ) {
                  <button
                    type="button"
                    class="dot"
                    [class.active]="i === vm.currentIndex"
                    (click)="goTo(i)">
                  </button>
                }

              </div>
            }

          </div>

          <!-- =========================
               THUMBNAILS
          ========================== -->

          @if (
            navigationMode === 'thumbnails' &&
            vm.images.length > 1
            ) {
            <div
              class="thumbnails"
              (wheel)="onThumbnailsWheel($event)">

              @for (
                item of vm.images;
                track trackByFilename(i, item);
                let i = $index
                ) {
                <button
                  type="button"
                  class="thumbnail"
                  [class.active]="i === vm.currentIndex"
                  (click)="goTo(i)">

                  @if (item.url && !item.error) {
                    <img
                      [src]="item.url"
                      [alt]="item.attachment.filename" />
                  }

                  @if (!item.url || item.error) {
                    <mat-icon class="thumbnail-error-icon">
                      broken_image
                    </mat-icon>
                  }

                  @if (allowFavorite && isFavorite(item)) {
                    <span class="thumbnail-favorite">
                      <mat-icon>star</mat-icon>
                    </span>
                  }

                </button>
              }

            </div>
          }

        </div>
      } @else {
        <div class="carousel-empty">
          <mat-icon>
            broken_image
          </mat-icon>
        </div>
      }
    }

  </div>
}
